import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { haptic } from '../engine/haptics.ts';
import { CORAL, CORAL_DEEP, CREAM, LINE, MUTED, PAPER, SHADOW_RAISED } from '../ui/theme.ts';

/** 600 ms hold ring, blur 8 → 0, reset on release, haptic tick every 100 ms, pop on complete. */
export function HoldReveal({ step, content, ctx }: StepProps) {
  const holdMs = step.holdMs ?? 600;
  const [pct, setPct] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const attempts = useRef(0);
  const raf = useRef<number | null>(null);
  const ticks = useRef(0);

  const projection = ctx.projection;
  const shared = ctx.shared.projection;

  useEffect(() => {
    ctx.setCta(revealed ? { label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', onPress: ctx.next } : { label: null });
  }, [step.id, revealed, content.cta]);

  useEffect(() => () => {
    if (raf.current) cancelAnimationFrame(raf.current);
  }, []);

  const start = (e: React.PointerEvent) => {
    if (revealed) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    attempts.current += 1;
    ticks.current = 0;
    const t0 = performance.now();
    const frame = () => {
      const p = Math.min(100, ((performance.now() - t0) / holdMs) * 100);
      setPct(p);
      const tick = Math.floor((p / 100) * (holdMs / 100));
      if (tick > ticks.current) {
        ticks.current = tick;
        haptic();
      }
      if (p >= 100) {
        setRevealed(true);
        haptic(20);
        ctx.event('hold_complete', { attempts: attempts.current });
        raf.current = null;
        return;
      }
      raf.current = requestAnimationFrame(frame);
    };
    raf.current = requestAnimationFrame(frame);
  };

  const stop = () => {
    if (revealed) return;
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = null;
    setPct(0);
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        role="button"
        tabIndex={0}
        aria-label={ctx.shared.lockLabel}
        style={{
          flex: 'none',
          position: 'relative',
          width: '100%',
          height: 220,
          borderRadius: 28,
          overflow: 'hidden',
          background: PAPER,
          boxShadow: SHADOW_RAISED,
          touchAction: 'none',
          cursor: 'pointer',
          userSelect: 'none',
          animation: revealed ? 'fk-pop .5s ease-out' : 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            filter: `blur(${Math.round(8 * (1 - pct / 100))}px)`,
            transition: 'filter .1s',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: CORAL_DEEP }}>{shared.label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.1 }}>{shared.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 'auto' }}>
            {projection.tiles.map((t, i) => (
              <div key={t.key} style={{ padding: 12, borderRadius: 16, background: CREAM }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>{shared.tiles[i]?.label}</div>
                <div style={{ fontWeight: 800, fontSize: 17 }}>
                  {t.fromLabel} → {t.toLabel}
                </div>
              </div>
            ))}
          </div>
        </div>

        {!revealed ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'rgba(255,255,255,.35)' }}>
            <span
              style={{
                position: 'relative',
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `conic-gradient(${CORAL} ${Math.round(pct * 3.6)}deg, ${LINE} 0)`,
              }}
            >
              <span style={{ width: 60, height: 60, borderRadius: '50%', background: PAPER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="lock-key" size={26} />
              </span>
            </span>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{ctx.shared.lockLabel}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>{pct > 0 && pct < 100 ? `${Math.round(pct)}%` : ''}</span>
          </div>
        ) : null}
      </div>
    </>
  );
}
